import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Simplified App Context State Interface
export interface AppContextType {
  // Core User and Subscription Data
  user: any;
  subscription: any;
  features: any;
  
  // Real-time and Notifications
  notifications: any[];
  realtimeConnection: any;
  alerts: any;
  
  // UI State and Preferences
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  loadingStates: any;
  errorStates: any;
  
  // Data and Caching
  cachedData: any;
  dataRefreshTimers: any;
  
  // User Preferences and Settings
  preferences: any;
  workspaceSettings: any;
  
  // Analytics and Tracking
  analytics: any;
  userBehavior: any;
  
  // Actions and Methods
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileMenuOpen: (open: boolean) => void;
  addNotification: (notification: any) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  updateUser: (user: any) => void;
  refreshSubscription: () => Promise<void>;
  updatePreferences: (preferences: any) => void;
  updateWorkspaceSettings: (settings: any) => void;
  refreshData: (dataType: string) => Promise<void>;
  clearCache: (dataType?: string) => void;
  trackEvent: (event: any) => void;
  trackPageView: (page: string) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleTheme: () => void;
  connectRealtime: () => Promise<void>;
  disconnectRealtime: () => void;
  subscribeToChannel: (channel: string, callback: (data: any) => void) => void;
  unsubscribeFromChannel: (channel: string) => void;
  showError: (error: any) => void;
  clearError: (errorId: string) => void;
  setLoading: (key: string, loading: boolean) => void;
  isFeatureEnabled: (feature: string) => boolean;
  canAccessFeature: (feature: string) => boolean;
  getTierLimits: () => any;
  checkRateLimit: (endpoint: string) => boolean;
  getApiUsage: () => any;
  updateApiUsage: (usage: any) => void;
}

// Initial State
const initialState = {
  user: null,
  subscription: null,
  features: {
    canAccessAdvancedAnalytics: false,
    canAccessBusinessIntelligence: false,
    canAccessForecasting: false,
    canCreateCustomAlerts: false,
    canCreateCustomReports: false,
    canAccessRealTimeData: false,
    canCreateCustomDashboards: false,
    canAccessWhiteLabelFeatures: false,
    canIntegrateCustomDataSources: false,
    canTrainCustomModels: false,
  },
  notifications: [],
  realtimeConnection: null,
  alerts: {
    activeAlerts: [],
    alertHistory: [],
    alertSettings: {
      globalEnabled: true,
      quietHours: {
        enabled: false,
        startTime: '22:00',
        endTime: '08:00',
        timezone: 'UTC',
      },
      notificationMethods: {
        email: true,
        push: true,
        sms: false,
        webhook: false,
        slack: false,
        teams: false,
      },
      frequency: 'immediate',
      grouping: true,
      groupingWindow: 5,
    },
    unreadCount: 0,
    lastChecked: new Date().toISOString(),
    isMonitoring: false,
  },
  theme: 'light' as const,
  sidebarCollapsed: false,
  mobileMenuOpen: false,
  loadingStates: {
    global: false,
    dashboard: false,
    trends: false,
    alerts: false,
    reports: false,
    analytics: false,
    settings: false,
    api: false,
    realtime: false,
  },
  errorStates: {
    global: null,
    api: null,
    network: null,
    authentication: null,
    authorization: null,
    data: null,
  },
  cachedData: {
    trends: { data: [], timestamp: '', ttl: 300000, version: '1.0' },
    alerts: { data: [], timestamp: '', ttl: 60000, version: '1.0' },
    reports: { data: [], timestamp: '', ttl: 300000, version: '1.0' },
    analytics: { data: null, timestamp: '', ttl: 60000, version: '1.0' },
    userData: { data: null, timestamp: '', ttl: 300000, version: '1.0' },
  },
  dataRefreshTimers: {
    trends: null,
    alerts: null,
    reports: null,
    analytics: null,
    realtime: null,
  },
  preferences: {
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    currency: 'USD',
    numberFormat: 'en-US',
    notifications: {},
    dashboard: {
      defaultView: 'grid',
      sidebarCollapsed: false,
      widgetLayout: [],
      refreshInterval: 300000,
      autoRefresh: true,
      showTutorials: true,
      showTips: true,
      compactMode: false,
      density: 'comfortable',
    },
    accessibility: {
      highContrast: false,
      reducedMotion: false,
      largeText: false,
      screenReader: false,
      keyboardNavigation: false,
      focusIndicators: true,
      colorBlindFriendly: false,
      dyslexiaFriendly: false,
    },
    privacy: {
      analytics: true,
      tracking: true,
      personalizedAds: false,
      dataSharing: false,
      cookieConsent: true,
      gdprCompliant: true,
      dataRetention: 365,
    },
    performance: {
      dataCaching: true,
      imageOptimization: true,
      lazyLoading: true,
      prefetching: true,
      compression: true,
      cacheStrategy: 'balanced',
    },
  },
  workspaceSettings: {
    organization: {
      name: '',
      logo: undefined,
      domain: undefined,
      industry: undefined,
      size: undefined,
    },
    branding: {
      primaryColor: '#7C3AED',
      secondaryColor: '#6366F1',
      logo: undefined,
      favicon: undefined,
      customCss: undefined,
    },
    integrations: {
      slack: undefined,
      teams: undefined,
      webhooks: [],
      apis: [],
    },
    security: {
      mfa: false,
      sessionTimeout: 3600,
      ipWhitelist: [],
      auditLogging: false,
    },
    compliance: {
      gdpr: false,
      ccpa: false,
      sox: false,
      hipaa: false,
    },
  },
  analytics: {
    events: [],
    pageViews: [],
    userSessions: [],
    featureUsage: [],
    performance: [],
    errors: [],
  },
  userBehavior: {
    clicks: [],
    scrolls: [],
    hovers: [],
    formInteractions: [],
    keyboardEvents: [],
    mouseMovements: [],
    focusEvents: [],
    resizeEvents: [],
  },
};

// Action Types
type AppAction = {
  type: string;
  payload?: any;
};

// Reducer
function appReducer(state: any, action: AppAction): any {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_SUBSCRIPTION':
      return { ...state, subscription: action.payload };
    case 'SET_FEATURES':
      return { ...state, features: action.payload };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter((n: any) => n.id !== action.payload),
      };
    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: [] };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.payload };
    case 'SET_MOBILE_MENU_OPEN':
      return { ...state, mobileMenuOpen: action.payload };
    case 'SET_LOADING':
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.payload.key]: action.payload.loading,
        },
      };
    case 'SET_ERROR':
      return {
        ...state,
        errorStates: {
          ...state.errorStates,
          [action.payload.key]: action.payload.error,
        },
      };
    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };
    case 'UPDATE_WORKSPACE_SETTINGS':
      return {
        ...state,
        workspaceSettings: { ...state.workspaceSettings, ...action.payload },
      };
    case 'TRACK_EVENT':
      return {
        ...state,
        analytics: {
          ...state.analytics,
          events: [...state.analytics.events, action.payload],
        },
      };
    case 'TRACK_PAGE_VIEW':
      return {
        ...state,
        analytics: {
          ...state.analytics,
          pageViews: [...state.analytics.pageViews, action.payload],
        },
      };
    case 'UPDATE_API_USAGE':
      return state;
    case 'UPDATE_CACHED_DATA':
      return {
        ...state,
        cachedData: {
          ...state.cachedData,
          [action.payload.type]: {
            ...state.cachedData[action.payload.type],
            data: action.payload.data,
            timestamp: action.payload.timestamp,
          },
        },
      };
    case 'CLEAR_CACHE':
      if (action.payload) {
        return {
          ...state,
          cachedData: {
            ...state.cachedData,
            [action.payload]: {
              data: null,
              timestamp: '',
              ttl: 0,
              version: '1.0',
            },
          },
        };
      }
      return {
        ...state,
        cachedData: initialState.cachedData,
      };
    default:
      return state;
  }
}

// Create Context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider Component
export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Actions
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: collapsed });
  }, []);

  const setMobileMenuOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_MOBILE_MENU_OPEN', payload: open });
  }, []);

  const addNotification = useCallback((notification: any) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
  }, []);

  const removeNotification = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  }, []);

  const clearAllNotifications = useCallback(() => {
    dispatch({ type: 'CLEAR_NOTIFICATIONS' });
  }, []);

  const updateUser = useCallback((user: any) => {
    if (state.user) {
      dispatch({ type: 'SET_USER', payload: { ...state.user, ...user } });
    }
  }, [state.user]);

  const refreshSubscription = useCallback(async () => {
    console.log('Refreshing subscription...');
  }, []);

  const updatePreferences = useCallback((preferences: any) => {
    dispatch({ type: 'UPDATE_PREFERENCES', payload: preferences });
  }, []);

  const updateWorkspaceSettings = useCallback((settings: any) => {
    dispatch({ type: 'UPDATE_WORKSPACE_SETTINGS', payload: settings });
  }, []);

  const refreshData = useCallback(async (dataType: string) => {
    console.log(`Refreshing ${dataType} data...`);
  }, []);

  const clearCache = useCallback((dataType?: string) => {
    dispatch({ type: 'CLEAR_CACHE', payload: dataType });
  }, []);

  const trackEvent = useCallback((event: any) => {
    dispatch({ type: 'TRACK_EVENT', payload: event });
  }, []);

  const trackPageView = useCallback((page: string) => {
    const pageView = {
      id: Math.random().toString(36).substr(2, 9),
      page,
      title: document.title,
      timestamp: new Date().toISOString(),
      duration: 0,
      userId: state.user?.id || 'anonymous',
      sessionId: 'session-id',
      referrer: document.referrer,
      properties: {},
    };
    dispatch({ type: 'TRACK_PAGE_VIEW', payload: pageView });
  }, [state.user?.id]);

  const setTheme = useCallback((theme: 'light' | 'dark' | 'system') => {
    dispatch({ type: 'SET_THEME', payload: theme });
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    dispatch({ type: 'SET_THEME', payload: newTheme });
  }, [state.theme]);

  const connectRealtime = useCallback(async () => {
    console.log('Connecting to realtime...');
  }, []);

  const disconnectRealtime = useCallback(() => {
    console.log('Disconnecting from realtime...');
  }, []);

  const subscribeToChannel = useCallback((channel: string, callback: (data: any) => void) => {
    console.log(`Subscribing to channel: ${channel}`);
  }, []);

  const unsubscribeFromChannel = useCallback((channel: string) => {
    console.log(`Unsubscribing from channel: ${channel}`);
  }, []);

  const showError = useCallback((error: any) => {
    dispatch({ type: 'SET_ERROR', payload: { key: 'global', error } });
  }, []);

  const clearError = useCallback((errorId: string) => {
    dispatch({ type: 'SET_ERROR', payload: { key: errorId, error: null } });
  }, []);

  const setLoading = useCallback((key: string, loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: { key, loading } });
  }, []);

  const isFeatureEnabled = useCallback((feature: string) => {
    // Get user plan from state.user or state.subscription
    const userPlan = state.user?.subscription?.plan_tier || state.user?.plan || 'free';
    
    // Define feature access by tier
    const featureAccess = {
      // Free tier features
      free: {
        canAccessAdvancedAnalytics: false,
        canAccessBusinessIntelligence: false,
        canAccessForecasting: false,
        canCreateCustomAlerts: false,
        canCreateCustomReports: false,
        canAccessRealTimeData: false,
        canCreateCustomDashboards: false,
        canAccessWhiteLabelFeatures: false,
        canIntegrateCustomDataSources: false,
        canTrainCustomModels: false,
      },
      // Starter tier features
      starter: {
        canAccessAdvancedAnalytics: false,
        canAccessBusinessIntelligence: false,
        canAccessForecasting: false,
        canCreateCustomAlerts: false,
        canCreateCustomReports: false,
        canAccessRealTimeData: false,
        canCreateCustomDashboards: false,
        canAccessWhiteLabelFeatures: false,
        canIntegrateCustomDataSources: false,
        canTrainCustomModels: false,
      },
      // Growth tier features
      growth: {
        canAccessAdvancedAnalytics: true,  // Enhanced analytics with momentum classification
        canAccessBusinessIntelligence: false,  // No business intelligence
        canAccessForecasting: true,  // 7-day trend predictions
        canCreateCustomAlerts: true,  // 7 smart alerts + basic webhooks
        canCreateCustomReports: false,  // Only weekly reports, not custom
        canAccessRealTimeData: false,  // No real-time data
        canCreateCustomDashboards: false,  // No custom dashboards
        canAccessWhiteLabelFeatures: false,  // No white-label features
        canIntegrateCustomDataSources: false,  // No custom data sources
        canTrainCustomModels: false,  // No custom models
      },
      // Professional tier features
      professional: {
        canAccessAdvancedAnalytics: true,
        canAccessBusinessIntelligence: true,  // Full business intelligence
        canAccessForecasting: true,
        canCreateCustomAlerts: true,
        canCreateCustomReports: true,  // Custom reports enabled
        canAccessRealTimeData: false,
        canCreateCustomDashboards: false,
        canAccessWhiteLabelFeatures: false,
        canIntegrateCustomDataSources: false,
        canTrainCustomModels: false,
      },
      // Business tier features
      business: {
        canAccessAdvancedAnalytics: true,
        canAccessBusinessIntelligence: true,
        canAccessForecasting: true,
        canCreateCustomAlerts: true,
        canCreateCustomReports: true,
        canAccessRealTimeData: true,  // Real-time data enabled
        canCreateCustomDashboards: true,  // Custom dashboards enabled
        canAccessWhiteLabelFeatures: false,
        canIntegrateCustomDataSources: true,  // Custom data sources enabled
        canTrainCustomModels: false,
      },
      // Enterprise tier features
      enterprise: {
        canAccessAdvancedAnalytics: true,
        canAccessBusinessIntelligence: true,
        canAccessForecasting: true,
        canCreateCustomAlerts: true,
        canCreateCustomReports: true,
        canAccessRealTimeData: true,
        canCreateCustomDashboards: true,
        canAccessWhiteLabelFeatures: true,  // White-label features enabled
        canIntegrateCustomDataSources: true,
        canTrainCustomModels: true,  // Custom models enabled
      },
    };
    
    const tierFeatures = featureAccess[userPlan as keyof typeof featureAccess] || featureAccess.free;
    return tierFeatures[feature as keyof typeof tierFeatures] || false;
  }, [state.user]);

  const canAccessFeature = useCallback((feature: string) => {
    return isFeatureEnabled(feature);
  }, [isFeatureEnabled]);

  const getTierLimits = useCallback(() => {
    return {
      apiRequests: { monthly: 1000, daily: 100, hourly: 10, perMinute: 1 },
      alerts: { total: 5, active: 3, custom: 2 },
      reports: { monthly: 1, custom: 0, whiteLabel: 0 },
      dashboards: { total: 1, custom: 0, shared: 0 },
      dataRetention: { days: 30, storage: 1 },
      integrations: { webhooks: 0, apis: 0, customDataSources: 0 },
      support: { priority: 'standard', responseTime: '24h', channels: ['email'] },
    };
  }, []);

  const checkRateLimit = useCallback((endpoint: string) => {
    return true;
  }, []);

  const getApiUsage = useCallback(() => {
    return {
      total_requests: 0,
      requests_this_month: 0,
      requests_today: 0,
      requests_this_hour: 0,
      rate_limit_remaining: 1000,
      rate_limit_reset: new Date().toISOString(),
      endpoint_usage: [],
      error_rate: 0,
      average_response_time: 0,
      p95_response_time: 0,
      p99_response_time: 0,
      success_rate: 100,
      bandwidth_usage: { total_mb: 0, this_month_mb: 0, this_day_mb: 0 },
      cost_breakdown: { total_cost: 0, this_month_cost: 0, projected_monthly_cost: 0, cost_by_endpoint: {} },
      usage_trends: { daily_usage: [], hourly_usage: [], weekly_usage: [] },
      performance_metrics: { uptime_percentage: 100, average_latency: 0, peak_latency: 0, concurrent_requests: 0, timeout_rate: 0 },
      alerts: { rate_limit_warnings: 0, error_spikes: 0, performance_degradation: 0, cost_thresholds: 0 },
      api_keys: [],
      quotas: { requests_per_month: 1000, requests_per_day: 100, requests_per_hour: 10, requests_per_minute: 1, bandwidth_per_month: 100, storage_per_month: 1 },
    };
  }, []);

  const updateApiUsage = useCallback((usage: any) => {
    dispatch({ type: 'UPDATE_API_USAGE', payload: usage });
  }, []);

  // Context value
  const contextValue: AppContextType = {
    ...state,
    setSidebarCollapsed,
    setMobileMenuOpen,
    addNotification,
    removeNotification,
    clearAllNotifications,
    updateUser,
    refreshSubscription,
    updatePreferences,
    updateWorkspaceSettings,
    refreshData,
    clearCache,
    trackEvent,
    trackPageView,
    setTheme,
    toggleTheme,
    connectRealtime,
    disconnectRealtime,
    subscribeToChannel,
    unsubscribeFromChannel,
    showError,
    clearError,
    setLoading,
    isFeatureEnabled,
    canAccessFeature,
    getTierLimits,
    checkRateLimit,
    getApiUsage,
    updateApiUsage,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}; 